import axios from 'axios'
import { cloneDeep } from 'lodash'

import {
  asyncReduxFilter,
  asyncReduxPromiseFilter
} from 'containers/Async/actions'
import {
  updateTables,
  clearTables,
} from 'containers/Entity/actions'
import {
  FORM_STATUS
} from 'containers/Project/constants'
import {
  CREATE_PROJECT_START,
  CLEAR_PROJECT_CREATE,
  INCREMENT_TAB_INDEX,
  UPDATE_REDIRECT,
  UPDATE_TAB_INDEX,
  TOGGLE_COLLAPSE,
  UPDATE_INPUT_VALUE,
  TOGGLE_COLLAPSE_CONTENT,
  UPDATE_PARTNER_SITE_SOURCE,
  UPDATE_PARTNER_SITE_FILTER,
  UPDATE_PRODUCT_HIERARCHY_FILTER,
  UPDATE_PRODUCT_HIERARCHY_SOURCE,
  RESET_PARTNER_SITE_FILTER,
  RESET_PRODUCT_HIERARCHY_FILTER,
  PartnerSiteEnum,
  ProductHierarchyEnum,
  ProductClassEnum,
  UPDATE_PRODUCT_CLASS_SOURCE,
  UPDATE_PRODUCT_CLASS_FILTER,
  RESET_PRODUCT_CLASS_FILTER,
  ENABLE_PRODUCT_CLASS,
  DISABLE_PRODUCT_CLASS,
} from './constants'
import {
  createNamedAction
} from 'helpers/ActionUtil'
import {
  get,
} from 'helpers/RequestUtil'
import {
  clearProjectInfoDetails,
  getProjectInfoAction,
  createProjectAction
} from 'containers/Project/Overview/actions'
import {
  partnerSiteIdSelector,
  productHierarchyIdSelector,
  productClassIdSelector
} from './selectors'
import {
  clearFormElements,
} from 'containers/Form/actions'
import {
  clearUiState,
  resetIsLoaded,
  loadProjectForm,
  setFormStatus
} from 'containers/Project/actions'
import {
  clearRisk,
} from 'containers/Form/RiskValidation/actions'
import {
  clearValidation,
} from 'containers/Form/Validation/actions'
import {
  error
} from 'containers/App/Notification/actions'

const createPartnerSiteAction = createNamedAction('PartnerSite')
const createProductClassAction = createNamedAction('ProductClass')

const getPartnerSitesUrl = '/api/fulcrum/partner_site_views'
const getPartnerSites = asyncReduxPromiseFilter(
  UPDATE_PARTNER_SITE_SOURCE,
  (product_hierarchy_ids) => {
    let url = getPartnerSitesUrl
    if (product_hierarchy_ids.length > 0)
      url = `${getPartnerSitesUrl}?filter[product_hierarchy_id]=${product_hierarchy_ids.join(',')}`
    return get(url)
  }
)

const getProductHierarchyUrl = '/api/fulcrum/product_hierarchy_views'
const getProductHierarchies = asyncReduxPromiseFilter(
  UPDATE_PRODUCT_HIERARCHY_SOURCE,
  (product_hierarchy_id = 1, partner_site_ids, depth) => {
    let url = `${getProductHierarchyUrl}/${product_hierarchy_id}/children`
    let params = []
    if (partner_site_ids)
      params = [...params, `filter[partner_site_id]=${partner_site_ids.join(',')}`]
    if (depth)
      params = [...params, `filter[depth]=${depth}`]
    url = `${url}?${params.join('&')}`
    return get(url)
  }
)

const getProductHierarchyAncestors = asyncReduxPromiseFilter(
  UPDATE_PRODUCT_HIERARCHY_SOURCE,
  (product_hierarchy_id) => {
    return get(`${getProductHierarchyUrl}/${product_hierarchy_id}/ancestors`)
  }
)

const getProductClassTypes = asyncReduxPromiseFilter(
  UPDATE_PRODUCT_CLASS_SOURCE,
  (product_hierarchy_id) => {
    return get(`/api/fulcrum/product_class_type_views?filter[product_hierarchy_id]=${product_hierarchy_id}`)
  }
)

const updateRedirect = redirect => ({ type: UPDATE_REDIRECT, redirect })

const clearProjectCreate = () => {
  return {
    type: CLEAR_PROJECT_CREATE
  }
}
const incrementTabIndex = () => {
  return {
    type: INCREMENT_TAB_INDEX
  }
}

const updateInputValue = (value) => {
  return {
    type: UPDATE_INPUT_VALUE,
    value
  }
}

const updateTabIndex = (index) => {
  return {
    type: UPDATE_TAB_INDEX,
    index
  }
}

const toggleCollapse = () => {
  return {
    type: TOGGLE_COLLAPSE,
  }
}

const toggleCollapseContent = () => {
  return {
    type: TOGGLE_COLLAPSE_CONTENT,
  }
}

const updatePartnerSiteSource = (source) => {
  return {
    type: UPDATE_PARTNER_SITE_SOURCE,
    source
  }
}

/*
 * Get most recently selected value
 * Which is the filter value that is equal to 1 on the lowest hierarchy
 * (i.e filter length === 1 at highest tab)
 */
const getCurrentFilterTab = (filterSource) => {
  return Object.values(ProductHierarchyEnum)
    .sort(({ valA }, { valB }) => valB - valA)
    .reduce((result, {label, value}) => {
      const { filters } = filterSource[label]
      let {
        filters: savedFilters,
        tab
      } = result

      // Only update if filter hasn't been found
      if (savedFilters.length > 1){
        if (filters.length === 1) {
          savedFilters = filters
          tab = value
        }
      } else if (savedFilters.length === 0) {
        savedFilters = filters
        tab = value
      }

      return {
        tab,
        filters: [...savedFilters]
      }
    }, { filters: [] , tab: 0})
}

const getCurrentFilter = (filterSource) => getCurrentFilterTab(filterSource).filters
const getCurrentTab = (filterSource) => getCurrentFilterTab(filterSource).tab

const getFilteredPartnerSiteData = () => {
  return (dispatch, getState) => {
    const {
      projectCreate: {
        productHierarchy
      }
    } = getState()
    const filters = getCurrentFilter(productHierarchy)
    return dispatch(getPartnerSites(filters))
  }
}

const getFilteredProductHierarchyData = (product_hierarchy_id, depth) => {
  return (dispatch, getState) => {
    const {
      projectCreate: {
        partnerSite: {
          filters
        }
      }
    } = getState()
    return dispatch(getProductHierarchies(product_hierarchy_id, filters, depth))
  }
}

const updatePartnerSiteData = () => {
  return dispatch => {
    return dispatch(getFilteredPartnerSiteData())
      .then(data => {
        return(dispatch(updateTables(data)))
      })
      .then(({ result: { partner_site_views } }) => partner_site_views)
      .then(source => dispatch(updatePartnerSiteSource(source)))
      .catch(e => dispatch(error('Failed to get partner sites.')))
  }
}

const updateProductHierarchyData = (type, product_hierarchy_id, depth) => {
  return dispatch => {
    return dispatch(getFilteredProductHierarchyData(product_hierarchy_id, depth))
      .then(data => dispatch(updateTables(data)))
      .then(({ result: { product_children_views } }) => product_children_views)
      .then(source => {
        return (dispatch(updateProductHierarchySource(type, source))) 
      })
      .catch(e => dispatch(error('Failed to get products.')))
  }
}

const updateProductClassTypeSource = (source) => {
  return {
    type: UPDATE_PRODUCT_CLASS_SOURCE,
    source
  }
}

/**
 *
 * Fetch Product Class data of current ID
 *
 * @returns {Promise}
 */
const updateProductClassType = () => {
  return (dispatch, getState) => {
    const productHierarchyId = productHierarchyIdSelector(getState())
    let promise = Promise.resolve(null)
    if (productHierarchyId) {
      // if product hierarchy id is determined, fetch the list of available product class type
      promise = dispatch(getProductClassTypes(productHierarchyId))
      .then(data => {
        dispatch(updateTables(data))
        return data
      })
      .then(({ result: { product_class_type_views } }) => product_class_type_views)
      .then(source => dispatch(updateProductClassTypeSource(source)))
      .then(() => dispatch(enableProductClass()))
      .catch(e => dispatch(error('Failed to get product classes.')))
    } else {
      // if product hierarchy id is not determined, clear current product class type data
      dispatch(resetProductClassFilter())
      dispatch(updateProductClassTypeSource({}))
      dispatch(disableProductClass())
    }
    return promise
  }
}


const getListOfProductHierarchyData = (queries, depth) => {
  return dispatch => {
    const queryPromises = queries.map(query => dispatch(getFilteredProductHierarchyData(query, depth)))
    return axios.all(queryPromises)
      .then( dataList => {
        const result = dataList
          .map(( { result } ) => result.product_children_views)
          .filter((val) => val)
          .reduce((final, result) => ([...final, ...result]), [])
        return { data: dataList, result: [...new Set(result)] }
      })
  }
}
/* recursively call update queries
 * 1. If current values is > 1 than stop
 * 2. If next level contains values than repeat for this level
 * 3. If values is a subset or contain result than continue
 * 4. If values are different stop
 * 5. If current values is > 1 than stop
 * 6. If next level contains values than repeat for this level
 */
const updateHierarchy = (queryList, productHierarchy) => {

  return dispatch => {
    let promise = Promise.resolve(null)
    // for all filters make an api call
    if (queryList.length > 0) {
      const [
        {
          type,
          filters,
          queries,
          depth
        },
        ...remaining
      ] = queryList

      const currentFilters = productHierarchy[type.label].filters
      promise = dispatch(getListOfProductHierarchyData(queries, depth))
        .then(({ data, result }) => {
          let promise = Promise.resolve(null)
          let toContinue = filters.every((filter) => result.includes(filter))
          if (toContinue) {
            if (filters.length === 1)
              promise = dispatch(updateHierarchy(remaining, productHierarchy))
          } else {
            data.forEach((value) => dispatch(updateTables(value)))
            promise = dispatch(updateProductHierarchySource(type, result))
          }
          return promise
        })
    }
    return promise
  }
}

export const updateProductHierarchyTabIndex = (() => {
  const updateTabAction = createNamedAction('ProductHierarchy', updateTabIndex)

  return (type) => {
    return (dispatch, getState) => {
      const {
        value: nextTab
      } = type
      const {
        projectCreate: {
          productHierarchy
        }
      } = getState()
      let promise = Promise.resolve(null)
      const filters = getCurrentFilter(productHierarchy)
      const filterTab = getCurrentTab(productHierarchy)
      const depth = nextTab - filterTab
      dispatch(updateTabAction(nextTab))
      if (depth > 0)
        promise = dispatch(updateProductHierarchyThanPartnerSite(type, filters, depth))
      else
        promise = dispatch(updateProductHierarchyThanPartnerSite(type, [1], nextTab + 1))
      return promise
    }
  }
})()

export const updatePartnerSiteFilterAction = (filters) => {
  return {
    type: UPDATE_PARTNER_SITE_FILTER,
    filters
  }
}

export const updatePartnerSiteFilter = asyncReduxFilter(
  UPDATE_PARTNER_SITE_FILTER,
  (filters) => {
    const incrementTabIndex = incrementPartnerSiteTabIndex
    return (dispatch, getState) => {
      dispatch(updatePartnerSiteFilterAction(filters))
      dispatch(incrementTabIndex())

      if (partnerSiteIdSelector(getState())) {
        dispatch(togglePartnerSiteCollapse())
        dispatch(togglePartnerSiteContent())
      }

      return dispatch(updatePartnerSiteThanProductHierarchy())
    }
  }
)

const resetPartnerSiteFilterAction = () => {
  return {
    type: RESET_PARTNER_SITE_FILTER,
  }
}

export const resetPartnerSiteFilter = () => {
  return dispatch => {
    dispatch(resetPartnerSiteFilterAction())
    return dispatch(updatePartnerSiteData())
      .then(() => dispatch(updatePartnerSiteThanProductHierarchy()))
  }
}

const resetProductHierarchyFilterAction = () => {
  return {
    type: RESET_PRODUCT_HIERARCHY_FILTER,
  }
}

export const resetProductHierarchyFilter = () => {
  return dispatch => {
    dispatch(resetProductHierarchyFilterAction())
    dispatch(updateProductClassType())
    return dispatch(updateProductHierarchyThanPartnerSite(ProductHierarchyEnum.SEGMENT, [1], 1))
  }
}

export const resetProductClassFilter = () => {
  return {
    type: RESET_PRODUCT_CLASS_FILTER,
  }
}

export const enableProductClass = () => {
  return {
    type: ENABLE_PRODUCT_CLASS,
  }
}

export const disableProductClass = () => {
  return {
    type: DISABLE_PRODUCT_CLASS,
  }
}

export const updateProductClassFilterAction = (filters) => {
  return {
    type: UPDATE_PRODUCT_CLASS_FILTER,
    filters
  }
}

export const updateProductHierarchySource = (() => {
  const updateSource = (source) => {
    return {
      type: UPDATE_PRODUCT_HIERARCHY_SOURCE,
      source
    }
  }

  const sourceActions = Object.keys(ProductHierarchyEnum)
    .reduce((result, key) => {
      const name = ProductHierarchyEnum[key].label
      return {
        ...result,
        [name]: createNamedAction(name, updateSource)
      }
    }, {})

  const getAction = (type) => {
    return sourceActions[type.label]
  }

  return (type, source) => {
    const action = getAction(type)
    return dispatch => dispatch(action(source))
  }
})()

const updateProductHierarchyAncestors = (child, [filter, ...rest]) => {
  return dispatch => {
    let promise = Promise.resolve(null)
    if (rest.length === 0) {
      promise = dispatch(getProductHierarchyAncestors(filter))
        .then(({ entities: { product_ancestor_views: entity }, result: { product_ancestor_views: result }}) => ({
          result: {
            product_children_views: result
          },
          entities: {
            product_children_views: entity
          }
        }))
    }
    return promise
  }
}

const updateProductHierarchyAncestorAction = (node, filters, getAction) => {
  return (dispatch, getState) => {
    const {
      projectCreate: {
        productHierarchy
      }
    } = getState()

    return dispatch(updateProductHierarchyThanPartnerSite(node, filters, 1))
      .then(() => dispatch(updateProductHierarchyAncestors(node, filters)))
      .then(data => {
        return (dispatch(updateTables(data)))
      })
      .then(data => {
        const {
          entities: {
            product_children_views
          }
        } = data
        const tabList = Object.values(ProductHierarchyEnum)
          .filter(({value}) => value < (node.value - 1))
          .reduce((result, type) => {
            const {label} = type
            const { filters } = productHierarchy[label]
            const update = filters.length === 0
            return [...result, { type, update }]
          }, [])

        const entity = Object.values(product_children_views)

        tabList.forEach(({type, update}) => {
          const ids = entity
            .filter(({rank}) => ((rank - 2) === type.value))
            .map(({ id }) => id)

          if (update)
            dispatch(updateProductHierarchySource(type, ids))
          else
            dispatch(getAction(type)(ids))
        })
        return data
      })
  }
}

export const updateProductHierarchyFilter = (() => {
  const updateFilters = (filters) => {
    return {
      type: UPDATE_PRODUCT_HIERARCHY_FILTER,
      filters
    }
  }

  const filterActions = Object.keys(ProductHierarchyEnum)
    .reduce((result, key) => {
      const name = ProductHierarchyEnum[key].label
      return {
        ...result,
        [name]: createNamedAction(name, updateFilters)
      }
    }, {})

  const getAction = (type) => {
    return filterActions[type.label]
  }

  const incrementProductHierarchyTabIndex = createNamedAction('ProductHierarchy', incrementTabIndex)
  return asyncReduxFilter(
    UPDATE_PRODUCT_HIERARCHY_FILTER,
    (parent, child, filters) => {
      const action = getAction(parent)
      return (dispatch, getState) => {
        const state = getState()
        const {
          projectCreate: {
            productHierarchy
          }
        } = state
        let promise = Promise.resolve(null)
        dispatch(action(filters))
        dispatch(incrementProductHierarchyTabIndex())
        if (child !== undefined)
          promise = dispatch(updateProductHierarchyThanPartnerSite(child, filters, 1)).then(() =>{
            let promise = Promise.resolve(null)
            if (filters.length === 1)
              promise = dispatch(updateProductHierarchyAncestorAction(child, filters, getAction))
                .then(() => {
                  if (productHierarchyIdSelector(getState())) {
                    dispatch(toggleProductHierarchyCollapse())
                    return dispatch(toggleProductHierarchyContent())
                  }
                })
                .then(() => dispatch(updateProductClassType()))

            return promise
          })
        return promise
      }
    }
  )
})()

export const updateProductClassFilter = ((filters) => {
  return (dispatch, getState) => {
    const updateDispatch = dispatch(updateProductClassFilterAction(filters))
    if (productClassIdSelector(getState())) {
      dispatch(toggleProductClassCollapse())
      return dispatch(toggleProductClassContent())
    }

    return updateDispatch
  }
})

export const clearInitialData = () => {
  return dispatch => {
    dispatch(clearProjectCreate())
    dispatch(clearTables())
    dispatch(clearFormElements())
    dispatch(clearProjectInfoDetails())
    dispatch(clearUiState())
    dispatch(resetIsLoaded())
    dispatch(clearValidation())
    dispatch(clearRisk())
  }
}

export const initializeData = () => {
  return dispatch => {
    return dispatch(updatePartnerSiteData())
      .then(() => dispatch(updateProductHierarchyData(ProductHierarchyEnum.SEGMENT, 1, 1)))
      .then(() => dispatch(updateProductClassType()))
  }
}

const getAllProductHierarchyData = (product_hierarchy_ids, depth) => {
  return dispatch => {
    return axios.all(product_hierarchy_ids.map(product_hierarchy_id =>
      dispatch(getFilteredProductHierarchyData(product_hierarchy_id, depth))
    ))
  }
}

export const updateProductHierarchyThanPartnerSite = (type, product_hierarchy_ids, depth) => {
  return (dispatch, getState) => {
    const { filters } = getState().projectCreate.partnerSite

    // update the product hierarchy data
    const promise = dispatch(getAllProductHierarchyData(product_hierarchy_ids, depth))
        .then(data => {
          data.forEach((value) => dispatch(updateTables(value)))
          return data
        })
        .then(data => data.map(({ result: { product_children_views }}) => product_children_views))
        .then(data => data.reduce((final, value) => [...final, ...value], []))
        .then(data => dispatch(updateProductHierarchySource(type, data)))

    // update partner site data
    return promise.then(() => dispatch(getFilteredPartnerSiteData()))
      .then((data) => {
        const { result: { partner_site_views: source } } = data
        let update = !filters.every((filter) => source.includes(filter))
        if (update) {
          dispatch(updateTables(data))
          dispatch(updatePartnerSiteSource(source))
        }
        return data
      })
  }
}

/*
 * 1. Get list of filters
 * 2. filter out list values that are empty
 * 3. Make API call using new partner site id and filters
 */
export const updatePartnerSiteThanProductHierarchy = () => {
  return (dispatch, getState) => {
    const {
      projectCreate: {
        productHierarchy
      }
    } = getState()

    // types are the different hierarchy levels
    const types = Object.values(ProductHierarchyEnum)
      .filter((type) => type !== ProductHierarchyEnum.BRAND)
      .filter(({ label }) => productHierarchy[label].filters.length !== 0)

    // get all queries required for the different product hierarchy levels
    const queryList = types.map((type, index, array) => {
      let queries = []
      let filters = productHierarchy[type.label].filters
      if (type === ProductHierarchyEnum.SEGMENT) {
        queries = [1]
      } else {
        const label = array[index - 1].label
        queries = productHierarchy[label].filters
      }
      return { type, filters, queries }
    })

    // recursive call for updating all levels of the hierarchy
    return dispatch(updateHierarchy(queryList, productHierarchy))
  }
}

export const updateProductHierarchyValue = (() => {
  const actions = Object.keys(ProductHierarchyEnum)
    .reduce((result, key) => {
      const name = ProductHierarchyEnum[key].label
      return {
        ...result,
        [name]: createNamedAction(name, updateInputValue)
      }
    }, {})
  const getAction = (type) => {
    return actions[type.label]
  }

  return (type, value) => {
    const action = getAction(type)
    return dispatch => dispatch(action(value))
  }
})()

const createProjectFunc = () => {
  return (dispatch, getState) => {
    const state = getState()
    const partnerSiteId = partnerSiteIdSelector(state)
    const productHierarchyId = productHierarchyIdSelector(state)
    const productClassId = productClassIdSelector(state)
    let promise = Promise.resolve(null)

    if (partnerSiteId && productHierarchyId) {
      dispatch(setFormStatus(FORM_STATUS.CREATE))
      dispatch(updateRedirect(true))

      promise = dispatch(createProjectAction(productHierarchyId, partnerSiteId, productClassId))
        .then(projectId => dispatch(loadProjectForm(projectId)))
    }

    return promise
  }
}

export const createProject = asyncReduxFilter(CREATE_PROJECT_START, createProjectFunc)
export const setRedirect = () => updateRedirect(false)
export const clearRedirect = () => updateRedirect(false)
export const toggleProductHierarchyCollapse = createNamedAction('ProductHierarchy', toggleCollapse)
export const toggleProductHierarchyContent = createNamedAction('ProductHierarchy', toggleCollapseContent)
export const togglePartnerSiteCollapse = createPartnerSiteAction(toggleCollapse)
export const togglePartnerSiteContent = createPartnerSiteAction(toggleCollapseContent)
export const toggleProductClassCollapse = createProductClassAction(toggleCollapse)
export const toggleProductClassContent = createProductClassAction(toggleCollapseContent)
export const incrementPartnerSiteTabIndex = createPartnerSiteAction(incrementTabIndex)
export const updatePartnerSiteTabIndex = createPartnerSiteAction(updateTabIndex)
export const updatePartnerValue = createNamedAction(PartnerSiteEnum.PARTNER.label, updateInputValue)
export const updatePartnerSiteValue = createNamedAction(PartnerSiteEnum.PARTNER_SITE.label, updateInputValue)
export const updateOdmValue = createNamedAction(PartnerSiteEnum.ODM.label, updateInputValue)
export const updateProductClassValue = createNamedAction(ProductClassEnum.PRODUCT_CLASS.label, updateInputValue)
